import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Shell from "../components/Shell.jsx";
import { api } from "../lib/api.js";
import { centsToDisplay, dollarsToCents, todayISO } from "../lib/money.js";
import { Field, inputCls } from "../components/ui.jsx";
import { ContactPicker } from "./Crm.jsx";

const STATUS_STYLE = {
  draft: "bg-slate-100 text-slate-500",
  sent: "bg-navy-700/10 text-navy-700",
  paid: "bg-emerald-100 text-emerald-700",
  void: "bg-slate-100 text-slate-400",
};

const SOURCE_WORDS = {
  stripe: "from the Stripe sync",
  import: "from a statement import",
  manual: "entered by hand",
  ocr: "from a receipt photo",
  email: "from an emailed receipt",
};

// One income entry that could be this invoice's payment.
//
// Everything shown here is RECORDED, not inferred: stripe_txns.income_txn_id
// names the ledger row a charge created. That is a stronger claim than the
// charge-to-payout pairing on /reconcile, which can only be guessed from
// amounts and dates — so this panel is allowed to state it plainly.
function Candidate({ c, number, onLink, busy }) {
  return (
    <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-3">
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-sm font-semibold text-navy-700">{centsToDisplay(c.amount_cents)}</p>
        <p className="text-xs text-slate-400">{c.date}</p>
      </div>
      <p className="mt-0.5 text-xs text-slate-500">
        {c.stripe?.payer || c.vendor || "No payer recorded"} · {SOURCE_WORDS[c.source] || c.source}
      </p>
      {c.stripe && (
        <p className="mt-1 text-xs text-slate-500">
          Stripe charge — {centsToDisplay(c.stripe.gross_cents)} gross, {centsToDisplay(c.stripe.fee_cents)} fee,{" "}
          {centsToDisplay(c.stripe.net_cents)} paid out.
        </p>
      )}
      {c.memo && <p className="mt-1 text-xs italic text-slate-400">{c.memo}</p>}
      {c.claimed_by ? (
        <p className="mt-2 rounded-lg bg-white px-2 py-1.5 text-xs text-slate-500">
          Already the payment for <span className="font-semibold">{c.claimed_by.number}</span> — detach it there first if it belongs here instead.
        </p>
      ) : (
        <button
          onClick={() => {
            if (
              !window.confirm(
                `Mark ${number} paid using the ${centsToDisplay(c.amount_cents)} entry from ${c.date}?\n\nNo new income is added — your books already hold this ${centsToDisplay(c.amount_cents)}. You can detach it again from this screen.`,
              )
            )
              return;
            onLink(c.id);
          }}
          disabled={busy}
          className="mt-2 w-full rounded-xl bg-emerald-600 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
        >
          {busy ? "Linking…" : "✓ This is the payment — link it"}
        </button>
      )}
    </div>
  );
}

// An income entry SHORT of the invoice total, which is almost always the
// Stripe payout: the deposit that lands after Stripe takes its fee. That is a
// transfer of money already counted, not the payment — linking it would mark
// the invoice paid while booking less income than it bills, and recreate the
// double count /reconcile exists to find. So it is named, explained, and
// deliberately not offered.
function NearMiss({ c, total, haveExact }) {
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
      <p className="text-sm font-semibold text-navy-700">
        {centsToDisplay(c.amount_cents)} <span className="text-xs font-normal text-slate-500">on {c.date}</span>
      </p>
      <p className="mt-1 text-xs text-slate-600">
        {centsToDisplay(c.difference_cents)} less than this invoice — the shape of a Stripe payout, which is the deposit that arrives
        after the fee comes off. That is a transfer of money already in your books, not the payment, so it cannot be linked here.
      </p>
      <p className="mt-1 text-xs text-slate-500">
        The payment itself books the full {centsToDisplay(total)} plus a separate fee expense.{" "}
        {haveExact ? "That is the entry above." : "Nothing here holds it yet — sync Stripe from the Money screen and come back."}
      </p>
    </div>
  );
}

export default function Invoice() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  // Editable working copy of the lines (draft/sent only).
  const [lines, setLines] = useState(null);
  const [paying, setPaying] = useState(false);
  const [payDate, setPayDate] = useState(todayISO());
  const [payMethod, setPayMethod] = useState("Check");
  const [drafting, setDrafting] = useState(false);
  const [emailNote, setEmailNote] = useState(null);
  // Income already in the books that could be this invoice's payment. Loaded
  // for an unpaid invoice so the warning is in front of him BEFORE he reaches
  // for "Record payment", which would book the money a second time.
  const [cands, setCands] = useState(null);

  const load = () =>
    api(`/api/invoices/${id}`)
      .then((d) => {
        setInv(d.invoice);
        setLines(d.invoice.lines.map((l) => ({ description: l.description, qty: String(l.qty), price: (l.unit_cents / 100).toFixed(2) })));
        if (d.invoice.status === "sent") api(`/api/invoices/${id}/payment-candidates`).then(setCands).catch(() => setCands(null));
        else setCands(null);
      })
      .catch(() => setErr("Invoice not found."));
  useEffect(() => {
    load();
  }, [id]);

  if (err)
    return (
      <Shell title="Invoice" back>
        <p className="text-sm text-red-600">{err}</p>
      </Shell>
    );
  if (!inv)
    return (
      <Shell title="Invoice" back>
        <p className="text-center text-sm text-slate-400">Loading…</p>
      </Shell>
    );

  const editable = inv.status === "draft" || inv.status === "sent";
  const setLine = (i, k, v) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)));
  const workingTotal = (lines || []).reduce((s, l) => s + (dollarsToCents(l.price) || 0) * (Number(l.qty) || 1), 0);

  async function put(body, note) {
    setBusy(true);
    setErr("");
    try {
      await api(`/api/invoices/${id}`, { method: "PUT", body });
      await load();
      if (note === "paid") setPaying(false);
    } catch (e) {
      setErr(e.message || "Save failed");
    } finally {
      setBusy(false);
    }
  }

  const saveLines = () =>
    put({
      lines: lines.filter((l) => l.description.trim()).map((l, idx) => ({ description: l.description, qty: Number(l.qty) || 1, unit_cents: dollarsToCents(l.price) || 0, sort: idx })),
    });

  async function remove() {
    if (!window.confirm(`Delete ${inv.number}?`)) return;
    await api(`/api/invoices/${id}`, { method: "DELETE" });
    navigate("/money?tab=invoices");
  }

  // Writes the email into Outlook's Drafts — PDF attached, Stripe link in the
  // body — and leaves the sending to Mike.
  async function draftEmail() {
    setDrafting(true);
    setEmailNote(null);
    try {
      const r = await api(`/api/invoices/${id}/email-draft`, { method: "POST" });
      setEmailNote({
        ok: true,
        text: `Draft saved in Outlook${r.to ? ` to ${r.to}` : " (no client email on file — add the recipient)"}, PDF attached${r.with_payment_link ? ", payment link in the body" : " — no payment link yet"}. Open Outlook → Drafts to review and send.`,
        link: r.web_link,
      });
    } catch (e) {
      setEmailNote({ ok: false, text: e.message || "Couldn't create the draft." });
    } finally {
      setDrafting(false);
    }
  }

  return (
    <Shell title={inv.number} back>
      <section className="rounded-2xl bg-white p-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-lg font-semibold text-navy-700">{centsToDisplay(inv.total_cents)}</p>
            <p className="text-xs text-slate-400">
              {inv.contact_company || inv.contact_name || "No client"} · issued {inv.issue_date}
              {inv.due_date ? ` · due ${inv.due_date}` : ""}
            </p>
          </div>
          <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase ${STATUS_STYLE[inv.status] || ""}`}>{inv.status}</span>
        </div>
        {inv.paid_at && <p className="mt-1 text-xs text-emerald-600">Paid {inv.paid_at.slice(0, 10)} — this invoice's income is in the ledger.</p>}
        {inv.status === "paid" && inv.paid_transaction_id && (
          <button
            onClick={() => {
              if (!window.confirm(`Detach the payment from ${inv.number}?\n\nThe income entry stays in your books — this only sets the invoice back to sent, so you can link the right entry instead. Nothing is deleted.`)) return;
              put({ unlink_payment: true });
            }}
            disabled={busy}
            className="mt-2 text-xs font-semibold text-slate-400 underline disabled:opacity-50"
          >
            Wrong payment? Detach it
          </button>
        )}
      </section>

      {editable && (
        <>
          <section className="space-y-3 rounded-2xl bg-white p-4 shadow-sm">
            <Field label="Bill to">
              <ContactPicker
                value={inv.contact_id ? { id: inv.contact_id, name: inv.contact_name, company: inv.contact_company } : null}
                onChange={(c) => put({ contact_id: c?.id || null })}
                allowCreate
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Issue date">
                <input type="date" defaultValue={inv.issue_date} onBlur={(e) => e.target.value !== inv.issue_date && put({ issue_date: e.target.value })} className={inputCls} />
              </Field>
              <Field label="Due date">
                <input type="date" defaultValue={inv.due_date || ""} onBlur={(e) => (e.target.value || null) !== inv.due_date && put({ due_date: e.target.value || null })} className={inputCls} />
              </Field>
            </div>
            <Field label="Notes / terms">
              <textarea defaultValue={inv.notes || ""} rows={2} onBlur={(e) => e.target.value !== (inv.notes || "") && put({ notes: e.target.value })} className={inputCls} />
            </Field>
            <Field label="Stripe payment link">
              <input
                type="url"
                inputMode="url"
                placeholder="https://buy.stripe.com/…"
                defaultValue={inv.payment_link || ""}
                onBlur={(e) => e.target.value.trim() !== (inv.payment_link || "") && put({ payment_link: e.target.value.trim() })}
                className={inputCls}
              />
            </Field>
            <p className="-mt-1 text-xs text-slate-400">Becomes a tap-to-pay button on the PDF and goes into the email.</p>
          </section>

          <section className="space-y-2 rounded-2xl bg-white p-4 shadow-sm">
            <p className="text-sm font-semibold text-slate-700">Line items</p>
            {(lines || []).map((l, i) => (
              <div key={i} className="grid grid-cols-[1fr_3.5rem_5.5rem] gap-2">
                <input value={l.description} onChange={(e) => setLine(i, "description", e.target.value)} className={inputCls} />
                <input value={l.qty} onChange={(e) => setLine(i, "qty", e.target.value)} inputMode="decimal" className={inputCls} />
                <input value={l.price} onChange={(e) => setLine(i, "price", e.target.value)} inputMode="decimal" className={inputCls} />
              </div>
            ))}
            <div className="flex items-center justify-between">
              <button type="button" onClick={() => setLines((ls) => [...ls, { description: "", qty: "1", price: "" }])} className="text-xs font-semibold text-navy-700">
                + Add line
              </button>
              <p className="text-sm font-semibold text-navy-700">Total {centsToDisplay(workingTotal)}</p>
            </div>
            <button onClick={saveLines} disabled={busy} className="w-full rounded-xl bg-navy-700 py-2.5 text-sm font-semibold text-white disabled:opacity-50">
              {busy ? "Saving…" : "Save lines"}
            </button>
          </section>
        </>
      )}

      {err && <p className="text-sm text-red-600">{err}</p>}

      <section className="space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <a
            href={`/api/invoices/${id}/pdf`}
            target="_blank"
            rel="noreferrer"
            className="block rounded-xl bg-navy-700 py-3 text-center text-sm font-semibold text-white"
          >
            📄 Invoice PDF
          </a>
          <button onClick={() => navigate(`/invoices/${id}/print`)} className="w-full rounded-xl border border-navy-600/30 bg-navy-700/5 py-3 text-sm font-semibold text-navy-700">
            🖨️ Print view
          </button>
        </div>
        <button onClick={draftEmail} disabled={drafting} className="w-full rounded-xl bg-navy-700 py-3 text-sm font-semibold text-white disabled:opacity-50">
          {drafting ? "Writing the draft…" : "✉️ Draft email in Outlook (PDF attached)"}
        </button>
        {emailNote && (
          <p className={`rounded-xl px-3 py-2 text-xs ${emailNote.ok ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-700"}`}>
            {emailNote.text}
            {emailNote.link && (
              <>
                {" "}
                <a href={emailNote.link} target="_blank" rel="noreferrer" className="font-semibold underline">
                  Open the draft ↗
                </a>
              </>
            )}
          </p>
        )}
        {inv.status === "draft" && (
          <button onClick={() => put({ status: "sent" })} disabled={busy} className="w-full rounded-xl bg-emerald-600 py-3 text-sm font-semibold text-white disabled:opacity-50">
            ✉️ Mark sent — starts the aging clock
          </button>
        )}
        {inv.status === "sent" && (cands?.exact?.length > 0 || cands?.near?.length > 0) && (
          <section className="space-y-2 rounded-2xl bg-white p-4 shadow-sm">
            <p className="text-sm font-semibold text-slate-700">{cands.exact.length > 0 ? "Already in your books" : "Checked your books first"}</p>
            {cands.exact.length > 0 ? (
              <p className="text-xs text-slate-500">
                This income is recorded already. Linking it marks the invoice paid and adds <span className="font-semibold">nothing</span> to
                your books — which is the difference between it and the Record button below.
              </p>
            ) : (
              <p className="text-xs text-slate-500">No entry for the full {centsToDisplay(cands.total_cents)} yet. One entry comes close:</p>
            )}
            {cands.exact.map((c) => (
              <Candidate key={c.id} c={c} number={inv.number} busy={busy} onLink={(txnId) => put({ link_payment: { transaction_id: txnId } }, "paid")} />
            ))}
            {cands.near.map((c) => (
              <NearMiss key={c.id} c={c} total={cands.total_cents} haveExact={cands.exact.length > 0} />
            ))}
          </section>
        )}
        {inv.status === "sent" && !paying && (
          <button
            onClick={() => setPaying(true)}
            className={`w-full rounded-xl py-3 text-sm font-semibold ${
              cands?.exact?.length ? "border border-slate-300 text-slate-500" : "bg-emerald-600 text-white"
            }`}
          >
            💵 Record a payment that is not in the books yet ({centsToDisplay(inv.total_cents)})
          </button>
        )}
        {paying && (
          <div className="space-y-2 rounded-2xl bg-white p-4 shadow-sm">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Payment date">
                <input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} className={inputCls} />
              </Field>
              <Field label="Method">
                <select value={payMethod} onChange={(e) => setPayMethod(e.target.value)} className={inputCls}>
                  {["Check", "ACH / bank transfer", "Stripe", "Cash", "Card", "Other"].map((m) => (
                    <option key={m}>{m}</option>
                  ))}
                </select>
              </Field>
            </div>
            <p className="text-xs text-slate-500">
              Adds a <span className="font-semibold">new</span> {centsToDisplay(inv.total_cents)} income entry to the books, linked to this
              invoice. If the payment is already recorded — a Stripe charge, a statement line — go back and link it instead, or you will
              count the same money twice.
            </p>
            <div className="flex gap-2">
              <button onClick={() => setPaying(false)} className="flex-1 rounded-xl border border-slate-300 py-2.5 text-sm font-semibold text-slate-500">
                Cancel
              </button>
              <button
                onClick={() => put({ record_payment: { date: payDate, payment_method: payMethod } }, "paid")}
                disabled={busy}
                className="flex-1 rounded-xl bg-emerald-600 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
              >
                {busy ? "Recording…" : "✓ Record"}
              </button>
            </div>
          </div>
        )}
        {editable && (
          <button onClick={() => put({ status: "void" })} disabled={busy} className="w-full rounded-xl border border-slate-300 py-3 text-sm font-semibold text-slate-500">
            Void invoice
          </button>
        )}
        <button onClick={remove} className="w-full rounded-xl border border-red-200 py-3 text-sm font-semibold text-red-600">
          Delete
        </button>
      </section>
    </Shell>
  );
}
